import type { HttpErrorResponse } from '@angular/common/http';

import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, concatMap, retry, tap } from 'rxjs/operators';
import { FeeItem } from 'src/app/shared/interfaces/account/account.interface';
import {
  IBatchRemittance,
  IProvider,
  IProviderAPI,
  IProviderStatusDates,
} from 'src/app/shared/interfaces/provider/provider.interfaces';
import { AlertService } from 'src/app/shared/services/alert.service';
import { ProviderGroupGQLService } from 'src/app/shared/services/provider/provider-group.graphql.service';
import { ProviderGQLService } from 'src/app/shared/services/provider/provider.graphql.service';
import { ProviderService } from 'src/app/shared/services/provider/provider.service';
import { UserStateService } from 'src/app/shared/services/users/user-state.service';
import { EMAIL_REGEX } from 'src/app/shared/variables/variables';

import { QualifiersGridComponent } from '../../qualifiers-grid/qualifiers-grid.component';

@Component({
  selector: 'edit-provider-modal',
  templateUrl: './edit-provider-modal.component.html',
  styleUrls: ['./edit-provider-modal.component.scss'],
})
export class EditProviderModalComponent implements OnInit, OnDestroy {
  @Input() providerId: string;
  @Input() isReadOnly = false;

  @ViewChild(QualifiersGridComponent) qualifiersGrid: QualifiersGridComponent;

  activeTabNumber = 1;

  providerForm: UntypedFormGroup;

  isLoading = true;
  isViewMode: boolean;
  isClientAdmin?: boolean;
  actionText: string;
  groupId: string;

  fees: FeeItem[];

  private isProviderSaved = false;

  private accountNumber: string;
  private pms: string;

  private providerStatusDates?: IProviderStatusDates;
  private batchRemittance?: IBatchRemittance;

  private provider?: IProviderAPI;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private alertService: AlertService,
    private fb: UntypedFormBuilder,
    private providerGroupGQLService: ProviderGroupGQLService,
    private srvProviders: ProviderGQLService,
    private srvProvidersAPI: ProviderService,
    private activeModal: NgbActiveModal,
    private userStateService: UserStateService,
  ) {}

  ngOnInit() {
    this.actionText = 'View';
    this.isViewMode = true;

    const currentUser = this.userStateService.user;
    const permissions = currentUser?.permissions;

    const containsAccounts = permissions?.some((permission) => {
      return (
        permission.name === 'Accounts' ||
        permission.name === 'Client Admin - Accounts'
      );
    });
    this.isClientAdmin = permissions?.some((permission) => {
      return permission.name === 'Client Admin - Accounts';
    });
    this.isViewMode = !containsAccounts;
    if (!this.isReadOnly) {
      this.isReadOnly = this.isViewMode;
    }
    if (!this.isViewMode) {
      this.actionText = 'Edit';
    }

    this.providerForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      mi: [''],
      degree: [''],
      pms: [{ value: '', disabled: true }],
      contact: this.fb.group({
        name: [''],
        phone: ['', Validators.maxLength(10)],
        fax: [''],
        email: ['', Validators.pattern(EMAIL_REGEX)],
        title: [''],
      }),
      ecsStatus: [''],
      ecsDate: [''],
      eraStatus: [''],
      eraDate: [''],
      claimStatusStatus: [''],
      claimStatusDate: [''],
      eligibilityStatus: [''],
      eligibilityDate: [''],
      statementStatus: [''],
      statementDate: [''],
      certified: [{ value: 'T', disabled: true }, Validators.required],
      feeSchedule: [
        { value: '', disabled: this.isClientAdmin },
        Validators.required,
      ],
      address: this.fb.group({
        line1: [''],
        line2: [''],
        zipcode: [''],
        state: [''],
        city: [''],
      }),
      notes: [''],
      alphaII: [''],
      appSenderCode: [''],
      backLink: [''],
      batch277ca: [''],
      batch277u: [''],
      batchCSV: [''],
      batchHumanReadable: [''],
      batchMachineReadable: [''],
      batchXml: [''],
      billingIds: [''],
      billingMethod: [''],
      combineERA: [''],
      eRAFileSeqNum: [''],
      fees: [''],
      fTPKey: [''],
      glueEM: [''],
      humanReadable271: [''],
      instAppSenderCode: [''],
      instNM109IdCode: [''],
      instProcessorId: [''],
      instSubmitterId: [''],
      interchangeNum: [''],
      lastUpdatedBy: [''],
      lockBox: [''],
      lockboxProcessorId: [''],
      mailboxId: [''],
      newStatements: [''],
      nM109IdCode: [''],
      organizationType: [''],
      pMIdentifier: [{ value: '', disabled: this.isClientAdmin }],
      pMSSoftware: [''],
      processorId: [''],
      recordStatus: [''],
      rejectPrintClaims: [''],
      remit277ca: [''],
      remit277u: [''],
      remitCsv: [''],
      remitCSVDaily: [''],
      remitEraSummary: [''],
      remitHumanReadable: [''],
      remitMachineReadable: [''],
      remitXml: [''],
      reportNumber: [''],
      statementMethod: [''],
      submitterId: [''],
      universalReport: [''],
    });

    this.subscriptions.add(
      combineLatest([
        this.providerGroupGQLService.getFeesIdList().pipe(
          retry(1),
          catchError((error) => {
            console.log(error);
            return of(error);
          }),
        ),
        this.srvProviders.getProvider(this.providerId),
      ]).subscribe({
        next: ([fees, provider]) => {
          this.provider = provider;
          this.groupId = provider.providerGroupId;

          this.fees = fees.nodes;

          const p: IProvider = provider;

          this.providerForm.patchValue({
            ...(this.provider as IProvider),
            address: {
              line1: p.addressLine1,
              line2: p.addressLine2,
              zipcode: p.addressZipcode,
              state: p.addressState,
              city: p.addressCity,
            },
            contact: {
              name: p.contactName,
              phone: p.contactPhone,
              fax: p.contactFax,
              email: p.contactEmail,
              title: p.contactTitle,
            },
            pms: p.pMS,
            mi: p.mI,
            eraStatus: p.eraStatus,
            ecsStatus: p.ecsStatus,
            ecsDate: p.ecsDate,
            eraDate: p.eraDate,
          });

          this.isLoading = false;
          if (this.isViewMode) {
            this.providerForm.disable();
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.alertService.error(error as HttpErrorResponse | string);
        },
      }),
    );
    if (this.providerStatusDates) {
      this.providerForm.patchValue(this.providerStatusDates);
    }
    if (this.pms) {
      this.providerForm.patchValue({ pms: this.pms });
    }
    if (this.batchRemittance) {
      this.providerForm.patchValue(this.batchRemittance);
    }
    if (this.isReadOnly) {
      this.providerForm.disable();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  close() {
    this.activeModal.dismiss();
  }

  save() {
    if (this.isViewMode) {
      this.activeModal.close(this.provider);
      return;
    }

    if (this.providerForm.invalid) {
      return;
    }

    this.isLoading = true;
    const pv = this.providerForm.getRawValue();
    const provider = {
      id: this.provider?.id,
      pms: pv.pms,
      accountNumber: this.accountNumber,
      groupId: this.groupId,
      lastName: pv.lastName,
      firstName: pv.firstName,
      name: pv.lastName,
      mi: pv.mi,
      degree: pv.degree,
      address: pv.address,
      contact: pv.contact,
      notes: pv.notes,
      feeSchedule: pv.feeSchedule,
      certified: pv.certified || 'T',
      ecsStatus: pv.ecsStatus,
      eraStatus: pv.eraStatus,
      claimStatusStatus: pv.claimStatusStatus,
      eligibilityStatus: pv.eligibilityStatus,
      statementStatus: pv.statementStatus,
    } as IProviderAPI;

    this.provider = { ...this.provider, name: pv.lastName, ...provider, ...pv };

    if (this.provider) {
      this.subscriptions.add(
        this.saveProvider(this.provider)
          .pipe(
            concatMap(() => {
              return this.qualifiersGrid.saveQualifiers(
                this.provider?.id,
                this.groupId,
              );
            }),
          )
          .subscribe({
            next: (result) => {
              this.isLoading = false;

              this.alertService.success('Provider updated successfully');
              if (result) {
                this.activeModal.close(this.provider);
              }
            },
            error: (error) => {
              this.isLoading = false;
              console.log(error);
              this.alertService.error(error as HttpErrorResponse | string);
            },
          }),
      );
    }
  }

  private saveProvider(provider: IProviderAPI): Observable<IProviderAPI> {
    if (this.isProviderSaved && this.provider) {
      return of<IProviderAPI>(this.provider);
    }

    return this.srvProvidersAPI.editProvider(provider).pipe(
      tap((provider) => {
        if (!this.provider) {
          this.provider = provider;
          this.provider.groupId = provider.providerGroupId;
          this.providerForm.patchValue(provider);
        }
      }),
    );
  }
}
